"use client";

import React from "react";
import { Typography } from "antd";
import { MemberTagBadges } from "@openings/template-components";
import {
  resolveRequirementLabel,
  resolveSelectionPaymentRequirement,
} from "@openings/booking-core";
import { AppointmentForm } from "@/lib/hooks/useAppointment";
import { useLocale } from "@/lib/i18n";
import { formatMinutes } from "@/lib/helpers/datetime";
import dayjs from "dayjs";
import { DEFAULT_DATETIME_FORMAT } from "@/lib/consts/formats";
import styles from "../appointmentFlow.module.css";

const { Text } = Typography;

const AppointmentReview = ({
  appointment,
}: {
  appointment: AppointmentForm;
}) => {
  const { t } = useLocale();
  if (!appointment) return null;
  const datetime = dayjs(`${appointment.date} ${appointment.time}`).format(
    DEFAULT_DATETIME_FORMAT,
  );
  const duration = appointment.services.reduce(
    (sum, service) =>
      sum + (service.option ? service.option.duration : service.duration),
    0,
  );
  const totalPrice = appointment.services.reduce(
    (sum, service) =>
      sum + (service.option ? service.option.price : service.price),
    0,
  );
  const priceIsFrom = appointment.services.some(
    (service) => !service.option && service.up,
  );
  const serviceLabel =
    appointment.services.length > 1 ? "Services" : "Service";
  // Shown before the contact details are collected, so a charge is never
  // first met on the card step (BF-DRIFT-005). The services carry this
  // provider's effective terms; exact amounts are BF-DRIFT-004.
  const paymentRequirementLabel = resolveRequirementLabel({
    paymentRequirement: resolveSelectionPaymentRequirement(
      appointment.services,
    ),
  });
  // `services` have already been run through `applyMemberPricing`, so this
  // is the provider's own (cloned) copy, not the business-level one. Prefer
  // the option's description when one is picked.
  const descriptions = appointment.services
    .map((service) =>
      (service.option?.description || service.description || "").trim(),
    )
    .filter(Boolean);
  return (
    <div className={styles.reviewCard}>
      <div className={styles.reviewRow}>
        <span className={styles.reviewLabel}>Provider</span>
        <Text className={styles.reviewValue}>{appointment.stylistName}</Text>
      </div>
      {appointment.services.map((service, i) => {
        const title = service.option
          ? `${service.title} (${service.option.title})`
          : service.title;
        return (
          <div key={`service-${i}`} className={styles.reviewRow}>
            <span className={styles.reviewLabel}>
              {i === 0 ? serviceLabel : ""}
            </span>
            <Text className={styles.reviewValue}>{title}</Text>
          </div>
        );
      })}
      <div className={styles.reviewRow}>
        <span className={styles.reviewLabel}>Price</span>
        <Text className={styles.reviewValue}>
          ${totalPrice}
          {priceIsFrom ? "+" : ""}
          {paymentRequirementLabel ? (
            <span data-booking-payment-requirement>
              {` · ${paymentRequirementLabel}`}
            </span>
          ) : null}
        </Text>
      </div>
      <div className={styles.reviewRow}>
        <span className={styles.reviewLabel}>Duration</span>
        <Text className={styles.reviewValue}>{formatMinutes(duration)}</Text>
      </div>
      <div className={styles.reviewRow}>
        <span className={styles.reviewLabel}>At</span>
        <Text className={styles.reviewValue}>{datetime}</Text>
      </div>
      {(appointment.scheduleName || appointment.locationName) && (
        <div className={styles.reviewRow}>
          <span className={styles.reviewLabel}>Location</span>
          <Text className={styles.reviewValue}>
            {appointment.scheduleName || appointment.locationName}
          </Text>
        </div>
      )}
      {descriptions.length > 0 && (
        <div className={styles.descriptionBlock}>
          <span className={styles.reviewLabel}>Description</span>
          {descriptions.map((description, i) => (
            <Text
              key={`description-${i}`}
              className={styles.serviceDescription}
            >
              {description}
            </Text>
          ))}
        </div>
      )}
      {/* Before commit, so a customer can still choose someone else. */}
      <div className={styles.memberNotes}>
        <MemberTagBadges tags={appointment.memberTags} label={t.memberNotes} />
      </div>
    </div>
  );
};

export default AppointmentReview;
